import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Cpu, FolderKanban, History, LogOut, Package, Rocket, UserCog, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { ROLE_LABELS } from '../api';
import { useAuth } from '../auth';

function NavItem({ to, icon, label }: { to: string; icon: ReactNode; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium ${
          isActive ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
        }`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 text-slate-900 md:flex-row">
      <aside className="flex shrink-0 flex-col bg-slate-900 md:min-h-screen md:w-56">
        <div className="flex items-center gap-2 px-4 py-4 text-white">
          <Cpu size={20} />
          <span className="font-semibold">FW Updater</span>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-2 pb-2 md:flex-1 md:flex-col md:pb-0">
          <NavItem to="/devices" icon={<Cpu size={16} />} label="Devices" />
          <NavItem to="/releases" icon={<Package size={16} />} label="Releases" />
          <NavItem to="/updates" icon={<Rocket size={16} />} label="Updates" />
          {user.role === 'admin' && (
            <>
              <NavItem to="/projects" icon={<FolderKanban size={16} />} label="Projects" />
              <NavItem to="/users" icon={<Users size={16} />} label="Users" />
              <NavItem to="/audit" icon={<History size={16} />} label="Audit log" />
            </>
          )}
        </nav>
        <div className="hidden border-t border-slate-800 p-2 md:block">
          <NavItem to="/account" icon={<UserCog size={16} />} label={user.name || user.username} />
          <p className="px-3 pb-1 text-xs text-slate-400">
            {user.username} · {ROLE_LABELS[user.role]}
          </p>
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-slate-300 hover:bg-slate-800/60 hover:text-white"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
        <div className="flex items-center justify-between border-t border-slate-800 px-4 py-2 text-sm text-slate-300 md:hidden">
          <NavLink to="/account" className="hover:text-white">
            {user.name || user.username} · {ROLE_LABELS[user.role]}
          </NavLink>
          <button type="button" onClick={handleLogout} className="flex items-center gap-1 hover:text-white">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 p-4 md:p-8">
        <Outlet />
      </main>
    </div>
  );
}
